import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TickerDisplayData } from '../../types/ticker';
import CategoryInfoButton from '../CategoryInfoButton';
import IndicatorInfoButton from '../IndicatorInfoButton';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getHeaderTextSizeClass, getSmallTextSizeClass, getStatisticLabelSizeClass } from '../../utils/fontSizeUtils';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { getListDailySmallScreenIndicatorsColumnWidth } from '../../utils/stockTableColumnUtils';

export interface Statistic {
  key: string;
  label: string;
  type?: 'ratio' | 'financial' | 'date';
  timeframe?: 'current' | 'previous' | 'annual';
}

// Lock icon component for pro-only features - clickable to navigate to pricing
export const LockIcon: React.FC<{ 
  className?: string; 
  onClick?: () => void;
  tooltipText?: string;
}> = ({ className = "w-4 h-4", onClick, tooltipText = "Upgrade to the Pro Plan to see" }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
  };

  // Show tooltip if provided (for both clickable and non-clickable icons)
  if (tooltipText) {
    return (
      <div className="relative group/lock inline-block">
        {onClick ? (
          <button
            onClick={handleClick}
            className="cursor-pointer hover:opacity-70 transition-opacity"
            aria-label="Upgrade to Pro"
          >
            <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        ) : (
          <div className="inline-block">
            <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        )}
        <div className="absolute hidden group-hover/lock:block left-1/2 -translate-x-1/2 bottom-full mb-2 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans whitespace-nowrap">
          {tooltipText}
        </div>
      </div>
    );
  }

  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2"/>
      <path d="M7 11V7a5 5 0 0110 0v4" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
};

export interface CategoryHeader {
  startIndex: number;
  label: string;
}

export interface StockTableProps {
  // Data
  tickerList: string[];
  tickerData: TickerDisplayData[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  
  // UI State
  isEditingTickers: boolean;
  hiddenStatistics: string[];
  
  // Drag and Drop State
  draggedTicker: string | null;
  dragOverTicker: string | null;
  droppedTicker: string | null;
  
  // Statistics Configuration
  statistics: Statistic[];
  categoryHeaders?: CategoryHeader[];
  getTickerData: (symbol: string) => TickerDisplayData | undefined;
  formatStatisticValue: (statisticKey: string, ticker: TickerDisplayData) => React.ReactNode;
  getStatisticColor: (statisticKey: string, ticker: TickerDisplayData) => string;
  
  // Pro-only locked statistics
  lockedStatistics?: string[];
  
  // Pro-only statistics that should always show PRO tag (regardless of subscription status)
  proOnlyStatistics?: string[];
  
  // Locked tickers (beyond free tier limit - e.g., beyond 10th position for free users)
  lockedTickers?: string[];
  
  // Event Handlers
  onRefetch: () => void;
  onTickerClick: (tickerSymbol: string) => void;
  onRemoveTicker: (ticker: string) => void;
  onDragStart: (e: React.DragEvent, ticker: string) => void;
  onDragOver: (e: React.DragEvent, ticker: string) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, ticker: string) => void;
  onDragEnd: () => void;
  onHideStatistic: (statisticKey: string) => void;
  onShowStatistic: (statisticKey: string) => void;
  onCategoryInfoClick?: (categoryName: string) => void;
  onIndicatorInfoClick?: (indicatorKey: string) => void;
  indicatorKeysWithInfo?: string[]; // Optional: if provided, only show info buttons for these keys. If not provided, show for all when onIndicatorInfoClick is provided.
  
  // UI Display Options
  showRefreshButton?: boolean;
  showDelayedPricesText?: boolean;
  showLastUpdatedDate?: boolean; // If true, show date and time; if false, show only time
  
  // Callback for full screen state changes
  onFullScreenChange?: (isFullScreen: boolean) => void;
  
  // Custom max height for scrollable container
  maxHeight?: string;
  
  // Custom ticker column width (defaults to 150px)
  tickerColumnWidth?: string;
  
  // Multiplier to reduce statistics column width (defaults to 1.0, use < 1.0 to make it narrower)
  statisticsColumnWidthMultiplier?: number;
  
  // Multiplier for full screen mode statistics column width (defaults to statisticsColumnWidthMultiplier if not provided)
  fullScreenStatisticsColumnWidthMultiplier?: number;

  /** On small screens, use the same Indicators column width as ListDaily. */
  useListDailySmallScreenColumnWidth?: boolean;
  
  // Optional: Public list mode (read-only)
  isPublicList?: boolean;

  /** Slightly more top padding on Pros/Cons rows (e.g. ListProsCons). */
  prosConsComfortablePadding?: boolean;
}

const StockTable: React.FC<StockTableProps> = ({
  // Debug: Log that component is rendering
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tickerList,
  tickerData,
  loading,
  error,
  lastUpdated,
  isEditingTickers,
  hiddenStatistics,
  draggedTicker,
  dragOverTicker,
  droppedTicker,
  statistics,
  categoryHeaders = [],
  getTickerData,
  formatStatisticValue,
  getStatisticColor,
  lockedStatistics = [],
  proOnlyStatistics = [],
  lockedTickers = [],
  onRefetch,
  onTickerClick,
  onRemoveTicker,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onHideStatistic,
  onShowStatistic,
  onCategoryInfoClick,
  onIndicatorInfoClick,
  indicatorKeysWithInfo,
  showRefreshButton = true,
  showDelayedPricesText = true,
  showLastUpdatedDate = false,
  onFullScreenChange,
  maxHeight = '80vh',
  tickerColumnWidth = '150px',
  statisticsColumnWidthMultiplier = 1.0,
  fullScreenStatisticsColumnWidthMultiplier,
  useListDailySmallScreenColumnWidth = false,
  isPublicList = false,
  prosConsComfortablePadding = false,
}) => {
  const prosConsCellPadding = prosConsComfortablePadding
    ? 'pt-2.5 pb-1.5 sm:pt-3 sm:pb-2'
    : 'py-1 sm:py-2';

  // Font size context
  const { fontSize } = useFontSize();
  const navigate = useNavigate();
  
  // Handler for lock icon clicks - navigate to pricing page (only for non-public lists)
  const handleLockIconClick = () => {
    if (!isPublicList) {
      navigate('/pricing', { state: { fromUpgrade: true } });
    }
  };
  
  // Internal state for removal animation
  const [removingTicker, setRemovingTicker] = useState<string | null>(null);
  
  // Promotional tooltip state for first-time ticker button click
  const [isTickerTooltipVisible, setIsTickerTooltipVisible] = useState(false);
  const [tickerTooltipPosition, setTickerTooltipPosition] = useState<{ tooltipLeft: number; arrowLeft: number } | null>(null);
  const firstTickerButtonRef = useRef<HTMLButtonElement | null>(null);
  
  // Check if user has clicked a ticker button before
  const hasClickedTickerButton = () => {
    return localStorage.getItem('has-clicked-ticker-button') === 'true';
  };
  
  // Check if tooltip has been displayed before
  const hasSeenTickerTooltip = () => {
    return localStorage.getItem('has-seen-ticker-tooltip') === 'true';
  };
  
  // Mark that user has clicked a ticker button
  const markTickerButtonClicked = () => {
    localStorage.setItem('has-clicked-ticker-button', 'true');
    setIsTickerTooltipVisible(false);
  };
  
  // Mark that tooltip has been displayed
  const markTickerTooltipSeen = () => {
    localStorage.setItem('has-seen-ticker-tooltip', 'true');
  };
  
  // Calculate tooltip position for first ticker button
  const calculateTickerTooltipPosition = useCallback(() => {
    if (!firstTickerButtonRef.current) return null;
    
    const button = firstTickerButtonRef.current;
    const buttonWidth = button.offsetWidth;
    const buttonCenter = buttonWidth / 2;
    
    // Position tooltip centered below the button
    // The tooltip's left edge will be positioned at the button's center, then we'll use transform to center it
    // Arrow will be at the center of the tooltip (50% from left)
    return { tooltipLeft: buttonCenter, arrowLeft: 50 };
  }, []);
  
  // Show tooltip if conditions are met
  useEffect(() => {
    if (
      tickerList.length === 1 && 
      !isEditingTickers && 
      !isPublicList && 
      !hasClickedTickerButton() &&
      !hasSeenTickerTooltip()
    ) {
      // Small delay to ensure button is rendered
      const timeoutId = setTimeout(() => {
        const position = calculateTickerTooltipPosition();
        if (position) {
          setIsTickerTooltipVisible(true);
          setTickerTooltipPosition(position);
          markTickerTooltipSeen();
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    } else {
      setIsTickerTooltipVisible(false);
      setTickerTooltipPosition(null);
    }
  }, [tickerList.length, isEditingTickers, isPublicList, calculateTickerTooltipPosition]);
  
  // Recalculate position on window resize
  useEffect(() => {
    if (!isTickerTooltipVisible) return;
    
    const handleResize = () => {
      const position = calculateTickerTooltipPosition();
      if (position) {
        setTickerTooltipPosition(position);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isTickerTooltipVisible, calculateTickerTooltipPosition]);
  
  // Dismiss tooltip when clicking anywhere on the page
  useEffect(() => {
    if (!isTickerTooltipVisible) return;
    
    const handleClick = () => {
      setIsTickerTooltipVisible(false);
      markTickerTooltipSeen();
    };
    
    // Add a small delay to avoid dismissing immediately when tooltip appears
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClick);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClick);
    };
  }, [isTickerTooltipVisible]);
  
  // Enhanced ticker click handler that marks button as clicked
  const handleTickerClickWithTracking = (symbol: string) => {
    markTickerButtonClicked();
    onTickerClick(symbol);
  };
  
  // Full screen state
  const [isFullScreen, setIsFullScreen] = useState(false);
  useBodyScrollLock(isFullScreen);

  // Handle keyboard events for full screen mode and add/remove body class
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    if (isFullScreen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.classList.add('full-screen-mode');
    } else {
      document.body.classList.remove('full-screen-mode');
    }
    
    // Notify parent of full screen state change
    if (onFullScreenChange) {
      onFullScreenChange(isFullScreen);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('full-screen-mode');
    };
  }, [isFullScreen, onFullScreenChange]);

  // Handle ticker removal with animation
  const handleRemoveTickerWithAnimation = (ticker: string) => {
    if (isEditingTickers) {
      // Start removal animation
      setRemovingTicker(ticker);
      
      // Remove from list after animation completes
      setTimeout(() => {
        onRemoveTicker(ticker);
        setRemovingTicker(null);
      }, 400); // Slightly longer for more delightful animation
    } else {
      // Normal mode, remove immediately
      onRemoveTicker(ticker);
    }
  };

  // State to track screen size for responsive column width
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);
  
  // Update screen size on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate effective full screen multiplier based on screen size
  // For full screen mode, we can use different multipliers for small vs medium/large screens
  const effectiveFullScreenMultiplier = useMemo(() => {
    if (fullScreenStatisticsColumnWidthMultiplier !== undefined) {
      // If a specific multiplier is provided, use it
      // For small screens, we can apply an additional reduction to allow wrapping
      // For medium/large screens, use the provided multiplier as-is
      return isSmallScreen 
        ? fullScreenStatisticsColumnWidthMultiplier * 0.75 // Additional 25% reduction for small screens
        : fullScreenStatisticsColumnWidthMultiplier;
    }
    return statisticsColumnWidthMultiplier;
  }, [fullScreenStatisticsColumnWidthMultiplier, statisticsColumnWidthMultiplier, isSmallScreen]);

  const dailyTabSmallScreenColumnWidthOptions = useMemo(
    () => ({
      statisticsColumnWidthMultiplier,
      fullScreenStatisticsColumnWidthMultiplier:
        fullScreenStatisticsColumnWidthMultiplier ?? statisticsColumnWidthMultiplier,
    }),
    [statisticsColumnWidthMultiplier, fullScreenStatisticsColumnWidthMultiplier]
  );

  const listDailySmallScreenColumnWidth = useMemo(
    () =>
      getListDailySmallScreenIndicatorsColumnWidth(
        isEditingTickers,
        false,
        dailyTabSmallScreenColumnWidthOptions
      ),
    [isEditingTickers, dailyTabSmallScreenColumnWidthOptions]
  );

  const listDailySmallScreenFullScreenColumnWidth = useMemo(
    () =>
      getListDailySmallScreenIndicatorsColumnWidth(
        isEditingTickers,
        true,
        dailyTabSmallScreenColumnWidthOptions
      ),
    [isEditingTickers, dailyTabSmallScreenColumnWidthOptions]
  );

  // Calculate dynamic width based on longest indicator name
  const calculateStatisticsColumnWidth = () => {
    if (isSmallScreen && useListDailySmallScreenColumnWidth) {
      return listDailySmallScreenColumnWidth;
    }

    const longestLabel = statistics.reduce(
      (longest, stat) => (stat.label.length > longest.length ? stat.label : longest),
      ''
    );

    // The first column header ("Indicators") must also fit; otherwise it can visually overlap
    // the first ticker column on small screens due to fixed table layout.
    const headerLabel = 'Indicators';
    const effectiveLabel = headerLabel.length > longestLabel.length ? headerLabel : longestLabel;
    
    // Check if any statistics have PRO tags (they're in proOnlyStatistics array)
    // PRO tag adds approximately 40px of width (text + padding + margin)
    const hasProTags = proOnlyStatistics.length > 0;
    const proTagSpace = hasProTags ? 40 : 0;
    
    // Base width calculation: character count * 8px + padding + edit button space + buffer + PRO tag space
    const baseWidth = effectiveLabel.length * 8; // ~8px per character
    const padding = 32; // 16px on each side (px-4)
    const editButtonSpace = isEditingTickers ? 24 : 0; // Space for edit button when visible
    const buffer = 20; // Extra buffer to prevent cutoff
    
    const calculatedWidth = baseWidth + padding + editButtonSpace + buffer + proTagSpace;
    
    // Use smaller width on small screens (mobile-first approach)
    // On small screens, use a more compact calculation
    if (isSmallScreen) {
      // Mobile: still ensure header fits. Keep padding realistic because the header uses `px-4`.
      const compactBaseWidth = effectiveLabel.length * 7.5; // slightly tighter than desktop, still readable
      const compactPadding = 32; // 16px on each side (px-4)
      const compactEditButtonSpace = isEditingTickers ? 16 : 0; // Smaller edit button space
      const compactBuffer = 12; // Smaller buffer for compact layout
      const compactProTagSpace = hasProTags ? 28 : 0; // Smaller PRO tag space on small screens
      
      const compactWidth = compactBaseWidth + compactPadding + compactEditButtonSpace + compactBuffer + compactProTagSpace;
      return Math.max(140, compactWidth * statisticsColumnWidthMultiplier); // Ensure header never overlaps
    }
    
    const finalWidth = calculatedWidth * statisticsColumnWidthMultiplier;
    return Math.max(120, finalWidth); // Minimum 120px on larger screens
  };
  
  const statisticsColumnWidth = calculateStatisticsColumnWidth();

  const fullScreenIndicatorsColumnWidth =
    isSmallScreen && useListDailySmallScreenColumnWidth
      ? listDailySmallScreenFullScreenColumnWidth
      : (isSmallScreen ? 120 : 150) * effectiveFullScreenMultiplier;
  
  // Styling constants
  
  // Ref and state for detecting when "Last Closing Price" row is stuck (normal mode)
  const lastClosingPriceRowRef = useRef<HTMLTableRowElement>(null);
  const [isLastClosingPriceStuck, setIsLastClosingPriceStuck] = useState(false);
  
  // Ref and state for detecting when "Last Closing Price" row is stuck (full screen mode)
  const lastClosingPriceRowFullScreenRef = useRef<HTMLTableRowElement>(null);
  const [isLastClosingPriceStuckFullScreen, setIsLastClosingPriceStuckFullScreen] = useState(false);
  
  // Ref for the scrollable table container (normal mode)
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  // Reset horizontal scroll to left when component mounts (e.g. when switching List tabs)
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollLeft = 0;
    }
  }, []);
  
  // Track previous ticker list to detect when a new ticker is added
  const prevTickerListRef = useRef<string[]>(tickerList);
  
  // Auto-scroll to the left when a new ticker is added
  useEffect(() => {
    // Check if a new ticker was added by comparing arrays
    const prevLength = prevTickerListRef.current.length;
    const currentLength = tickerList.length;
    
    if (currentLength > prevLength && tableContainerRef.current) {
      // Scroll to the left (scrollLeft = 0) to reset view to the beginning
      // Use setTimeout with longer delay to ensure the DOM has fully updated with the new column
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollLeft = 0;
        }
      }, 200);
    }
    // Update the ref to track current list
    prevTickerListRef.current = tickerList;
  }, [tickerList]);
  
  // Detect when the sticky row becomes stuck using scroll event (normal mode)
  useEffect(() => {
    const row = lastClosingPriceRowRef.current;
    if (!row || isFullScreen) return; // Only check in normal mode
    
    const checkIfStuck = () => {
      if (!row) return;
      
      // Find the header row to get its position
      const table = row.closest('table');
      const thead = table?.querySelector('thead');
      const headerRect = thead?.getBoundingClientRect();
      
      const rowRect = row.getBoundingClientRect();
      const stickyTop = window.innerWidth >= 640 ? 64 : 48; // sm:top-[64px] or top-[48px]
      
      // Row is stuck if:
      // 1. The header exists and the row is positioned right below it (header bottom + small gap)
      // 2. OR the row's top is at the sticky top value
      let isStuck = false;
      
      if (headerRect) {
        // Check if row is positioned right below the header (accounting for header height + 1px border)
        const headerBottom = headerRect.bottom;
        const expectedRowTop = headerBottom; // Should be right at the header bottom
        const tolerance = 2;
        isStuck = Math.abs(rowRect.top - expectedRowTop) <= tolerance;
      } else {
        // Fallback: check if row is at sticky top position
        isStuck = Math.abs(rowRect.top - stickyTop) <= 3;
      }
      
      setIsLastClosingPriceStuck(isStuck);
    };
    
    // Use Intersection Observer as primary method
    const observer = new IntersectionObserver(
      (entries) => {
        // This fires when the row enters/exits the viewport
        checkIfStuck();
      },
      {
        root: null,
        rootMargin: `-${window.innerWidth >= 640 ? 64 : 48}px 0px 0px 0px`,
        threshold: [0, 1],
      }
    );
    
    observer.observe(row);
    
    // Also listen to scroll events as backup
    const scrollContainer = row.closest('.overflow-y-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkIfStuck, { passive: true });
    }
    window.addEventListener('scroll', checkIfStuck, { passive: true });
    
    // Check initially and periodically
    checkIfStuck();
    const intervalId = setInterval(checkIfStuck, 100);
    
    return () => {
      observer.disconnect();
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkIfStuck);
      }
      window.removeEventListener('scroll', checkIfStuck);
      clearInterval(intervalId);
    };
  }, [statistics, isFullScreen]);
  
  // Detect when the sticky row becomes stuck using scroll event (full screen mode)
  useEffect(() => {
    const row = lastClosingPriceRowFullScreenRef.current;
    if (!row || !isFullScreen) return; // Only check in full screen mode
    
    const checkIfStuck = () => {
      if (!row) return;
      
      // Find the header row to get its position
      const table = row.closest('table');
      const thead = table?.querySelector('thead');
      const headerRect = thead?.getBoundingClientRect();
      
      const rowRect = row.getBoundingClientRect();
      // In full screen mode, header is h-12 sm:h-16 (48px/64px) and row sticks at top-[48px] sm:top-[64px]
      // Use header height dynamically instead of hardcoded value
      const stickyTop = headerRect ? headerRect.height : (window.innerWidth >= 640 ? 64 : 48);
      
      // Row is stuck if:
      // 1. The row's top is at or very close to the sticky top position
      // 2. AND the row is actually visible (not scrolled above viewport)
      let isStuck = false;
      
      // First check if row is visible (top should be >= 0 or close to sticky position)
      const isRowVisible = rowRect.top >= -10; // Allow small negative for edge cases
      
      if (isRowVisible && headerRect) {
        // Check if row is positioned right below the header
        const headerBottom = headerRect.bottom;
        const tolerance = 2;
        isStuck = Math.abs(rowRect.top - headerBottom) <= tolerance;
        
        // Also check if row is at the sticky top position as a fallback
        if (!isStuck) {
          isStuck = Math.abs(rowRect.top - stickyTop) <= 3;
        }
        
      } else if (!isRowVisible) {
        // Row is scrolled above viewport, definitely not stuck
        isStuck = false;
      } else {
        // Fallback: check if row is at sticky top position
        isStuck = Math.abs(rowRect.top - stickyTop) <= 3 && rowRect.top >= 0;
      }
      
      setIsLastClosingPriceStuckFullScreen(isStuck);
    };
    
    // Use Intersection Observer as primary method
    const observer = new IntersectionObserver(
      (entries) => {
        checkIfStuck();
      },
      {
        root: null,
        rootMargin: `-${window.innerWidth >= 640 ? 64 : 48}px 0px 0px 0px`,
        threshold: [0, 1],
      }
    );
    
    observer.observe(row);
    
    // Also listen to scroll events as backup
    const scrollContainer = row.closest('.h-full.overflow-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkIfStuck, { passive: true });
    }
    window.addEventListener('scroll', checkIfStuck, { passive: true });
    
    // Check initially and periodically
    checkIfStuck();
    const intervalId = setInterval(checkIfStuck, 100);
    
    return () => {
      observer.disconnect();
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', checkIfStuck);
      }
      window.removeEventListener('scroll', checkIfStuck);
      clearInterval(intervalId);
    };
  }, [statistics, isFullScreen]);

  return (
    <>
    {/* Table area */}
    <div ref={tableContainerRef} className="w-full overflow-x-auto overflow-y-auto" style={{ maxHeight: maxHeight, position: 'relative' }}>
        <table 
          className="min-w-full font-mono" 
          style={{ 
            tableLayout: 'fixed', 
            borderCollapse: 'collapse'
          }}
        >
          <colgroup>
            {/* fix the first column width based on longest indicator name */}
            <col style={{ width: `${statisticsColumnWidth}px`, minWidth: `${statisticsColumnWidth}px` }} />
            {/* data columns - width configurable via tickerColumnWidth prop, smaller on small screens */}
            {tickerList.map((_, index) => {
              // Reduce ticker column width on small screens
              const effectiveTickerWidth = isSmallScreen 
                ? `${Math.max(100, parseInt(tickerColumnWidth.replace('px', '')) * 0.75)}px` // 75% of original width, minimum 100px
                : tickerColumnWidth;
              return (
                <col key={index} style={{ width: effectiveTickerWidth, minWidth: effectiveTickerWidth }} />
              );
            })}
          </colgroup>

          {/* ---------- TABLE HEADER (first row) ---------- */}
          <thead 
            className="sticky-header-border border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-background-card"
            style={{ 
              position: 'sticky', 
              top: -1, 
              zIndex: 50, 
              margin: 0, 
              padding: 0,
              isolation: 'isolate'
            }}
          >
            <tr className="h-12 sm:h-16 border-b border-gray-200 dark:border-border-color bg-gray-50 dark:bg-background-card" style={{ margin: 0, padding: 0 }}>
              {/* top-left corner: Statistics header */}
              <th 
                className={`px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider bg-gray-50 dark:bg-background-card h-12 sm:h-16`}
                style={{ position: 'sticky', left: 0, zIndex: 40 }}
              >
                <div className="flex items-center h-full">
                  Indicators
                </div>
              </th>

              {/* ticker headers */}
              {tickerList.map((symbol) => {
                const ticker = getTickerData(symbol);
                return (
                  <th
                    key={symbol}
                    className={`px-0.5 sm:px-1 md:px-2 lg:px-4 text-left ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card group transition-all duration-200 ${
                      isEditingTickers ? 'cursor-move' : ''
                    } ${
                      dragOverTicker === symbol ? 'bg-highlight-gold/20 border-l-4 border-highlight-gold' : ''
                    } ${
                      draggedTicker === symbol ? 'opacity-60 scale-95 bg-highlight-gold/10' : ''
                    } ${
                      removingTicker === symbol ? '' : ''
                    } ${
                      droppedTicker === symbol ? 'drop-in-animation bg-highlight-gold/15' : ''
                    }`}
                    draggable={isEditingTickers}
                    onDragStart={(e) => onDragStart(e, symbol)}
                    onDragOver={(e) => onDragOver(e, symbol)}
                    onDragLeave={onDragLeave}
                    onDrop={(e) => onDrop(e, symbol)}
                    onDragEnd={onDragEnd}
                  >
                    <div className="flex items-center gap-2 h-full">
                      <div className="relative group/button">
                        <button
                          ref={tickerList.length === 1 && !isEditingTickers && !isPublicList && !hasClickedTickerButton() && !hasSeenTickerTooltip() ? firstTickerButtonRef : null}
                          className={`inline-block bg-gray-200 dark:bg-border-color text-gray-900 dark:text-text-primary px-3 py-1 rounded-md transition-all duration-200 ${
                            isEditingTickers 
                              ? 'cursor-move' 
                              : lockedTickers.includes(symbol)
                              ? 'cursor-not-allowed opacity-60'
                              : 'hover:bg-primary dark:hover:bg-highlight-gold hover:text-gray-900 dark:hover:text-background'
                          }`}
                          onClick={isEditingTickers || lockedTickers.includes(symbol) ? undefined : () => handleTickerClickWithTracking(symbol)}
                          disabled={isEditingTickers || lockedTickers.includes(symbol)}
                          title={lockedTickers.includes(symbol) ? 'Upgrade to Pro to access this ticker' : undefined}
                        >
                          {symbol}
                        </button>
                        <div className="absolute hidden group-hover/button:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans">
                          {ticker?.ticker_name || ticker?.name || symbol}
                        </div>
                        
                        {/* Promotional tooltip for first-time ticker button click */}
                        {isTickerTooltipVisible && tickerTooltipPosition && tickerList.length === 1 && symbol === tickerList[0] && (
                          <div 
                            className="absolute top-full mt-2 z-[9999] pointer-events-none"
                            style={{
                              left: `${tickerTooltipPosition.tooltipLeft}px`,
                              transform: 'translateX(-50%)'
                            }}
                          >
                            <div className="bg-background-card dark:bg-gray-700 border border-border-color dark:border-gray-600 rounded-lg shadow-lg px-4 py-3 max-w-[280px] sm:w-[500px] relative pointer-events-auto font-sans">
                              {/* Arrow pointing up to the middle of ticker button - centered in tooltip */}
                              <div 
                                className="absolute -top-2 left-1/2 w-0 h-0"
                                style={{
                                  transform: 'translateX(-50%)'
                                }}
                              >
                                <div className="border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-background-card dark:border-b-gray-700"></div>
                              </div>
                            
                              <div className="flex items-start gap-3">
                                {/* Lightbulb icon */}
                                <div className="flex-shrink-0 w-10 h-10 bg-primary dark:bg-highlight-gold rounded-full flex items-center justify-center relative">
                                  <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                  </svg>
                                </div>
                                
                                {/* Text content */}
                                <div className={`text-white text-sm normal-case font-sans font-normal ${getTextSizeClass(fontSize)}`}>
                                  Click on the ticker to view detailed history.
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {isEditingTickers && (
                        <button
                          onClick={() => handleRemoveTickerWithAnimation(symbol)}
                          className={`text-gray-600 dark:text-text-secondary hover:text-market-red transition-all duration-200 p-1 rounded-full hover:bg-market-red/10 active:scale-90 ${
                            removingTicker === symbol ? 'opacity-0 scale-75' : ''
                          }`}
                          title="Remove ticker"
                          disabled={removingTicker === symbol}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
              
              {/* Full screen button - sticky to the right */}
              <th 
                className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                style={{ position: 'sticky', right: 0, zIndex: 40 }}
              >
                {!isEditingTickers && (
                  <button
                    onClick={() => setIsFullScreen(true)}
                    className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                    title="Open in full screen"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </button>
                )}
              </th>
            </tr>
          </thead>

          {/* ---------- TABLE BODY ---------- */}
          <tbody className="divide-y divide-gray-200 dark:divide-border-color">
            {/* Each row corresponds to one statistic. First cell (td) is the statistic label and sticky left. */}
            {(() => {
              // Use the same pre-calculation approach as full screen mode
              const filteredStats = statistics.filter(statistic => isEditingTickers || !hiddenStatistics.includes(statistic.key));
              const baseHeaders = categoryHeaders || [];
              
              // Pre-calculate category headers like TickerFundamentals and TickerAnalystViews do
              const calculatedHeaders = baseHeaders.map(header => {
                if (isEditingTickers) {
                  const statAtOriginalIndex = statistics[header.startIndex];
                  if (!statAtOriginalIndex) {
                    return { ...header, startIndex: -1 };
                  }
                  const newStartIndex = filteredStats.findIndex(stat => stat.key === statAtOriginalIndex.key);
                  return {
                    ...header,
                    startIndex: newStartIndex >= 0 ? newStartIndex : -1
                  };
                } else {
                  const nextCategoryStartIndex = baseHeaders.find(h => h.startIndex > header.startIndex)?.startIndex || statistics.length;
                  const categoryStatistics = statistics.slice(header.startIndex, nextCategoryStartIndex);
                  const visibleStatsInCategory = categoryStatistics.filter(stat => !hiddenStatistics.includes(stat.key));
                  
                  if (visibleStatsInCategory.length === 0) {
                    return { ...header, startIndex: -1 };
                  }
                  
                  const firstVisibleStatistic = visibleStatsInCategory[0];
                  const newStartIndex = filteredStats.findIndex(stat => stat.key === firstVisibleStatistic.key);
                  
                  if (newStartIndex < 0) {
                    return { ...header, startIndex: -1 };
                  }
                  
                  return {
                    ...header,
                    startIndex: newStartIndex
                  };
                }
              }).filter(header => {
                return isEditingTickers || header.startIndex !== -1;
              });
              
              // Track which section headers have already been shown to avoid duplicates
              const shownHeaders = new Set<string>();
              
              return filteredStats.map((statistic, index) => {
                  const isHidden = hiddenStatistics.includes(statistic.key);
                  
                  // Check if we need to render a category header before this statistic
                  // Find the header that should appear at this index
                  const categoryHeader = calculatedHeaders.find(header => {
                    if (shownHeaders.has(header.label)) {
                      return false;
                    }
                    if (index !== header.startIndex) {
                      return false;
                    }
                    shownHeaders.add(header.label);
                    return true;
                  });
                  
                  return (
                    <React.Fragment key={statistic.key}>
                      {/* Category header row */}
                      {categoryHeader && (
                      <tr className="bg-gray-100 dark:bg-border-color">
                        {/* Sticky category header label */}
                        <td 
                          className={`py-1 sm:py-2 px-4 text-gray-900 dark:text-text-primary font-semibold ${getTextSizeClass(fontSize)} bg-gray-100 dark:bg-border-color`}
                          style={{ 
                            position: 'sticky', 
                            left: 0, 
                            zIndex: 30,
                            width: `${statisticsColumnWidth}px`,
                            maxWidth: `${statisticsColumnWidth}px`
                          }}
                          title={categoryHeader.label}
                        >
                          <div 
                            className="whitespace-nowrap"
                            style={{ overflow: 'visible', position: 'relative' }}
                          >
                            <span className="inline">{categoryHeader.label}</span>
                            {onCategoryInfoClick && (
                              <CategoryInfoButton
                                categoryName={categoryHeader.label}
                                onCategoryInfoClick={onCategoryInfoClick}
                              />
                            )}
                          </div>
                        </td>
                        {/* Category header cells for each ticker */}
                        {tickerList.map((symbol) => (
                          <td
                            key={symbol}
                            className={`py-1 sm:py-2 px-0.5 sm:px-1 md:px-2 lg:px-4 whitespace-nowrap text-gray-900 dark:text-text-primary font-semibold ${getTextSizeClass(fontSize)} bg-gray-100 dark:bg-border-color`}
                          >
                            {/* Empty cell for ticker column */}
                          </td>
                        ))}
                        
                        {/* Empty cell for full screen button column */}
                        <td className={`py-1 sm:py-2 px-1 sm:px-2 md:px-4 text-gray-900 dark:text-text-primary font-semibold ${getTextSizeClass(fontSize)} bg-gray-100 dark:bg-border-color`}>
                          {/* Empty cell for full screen button column */}
                        </td>
                      </tr>
                      )}
                    
                    {/* Statistic row */}
                    <tr 
                      ref={statistic.key === 'last_closing_price' ? lastClosingPriceRowRef : null}
                      className={`h-12 sm:h-16 ${statistic.key === 'last_closing_price' ? `sticky top-[47px] sm:top-[63px] z-40 sticky-last-closing-price ${isLastClosingPriceStuck ? 'is-stuck' : ''}` : ''}`}
                    >
                    {/* sticky left statistic label */}
                    <td 
                      className={`px-4 text-gray-900 dark:text-text-primary font-medium ${statistic.key === 'last_closing_price' && isLastClosingPriceStuck ? 'bg-blue-50 dark:bg-gray-800' : 'bg-surface-light dark:bg-background-card'} ${statistic.key === 'pros' || statistic.key === 'cons' ? `align-top ${prosConsCellPadding}` : ''}`}
                      style={{ 
                        position: 'sticky', 
                        left: 0, 
                        zIndex: 30, 
                        width: `${statisticsColumnWidth}px`,
                        maxWidth: `${statisticsColumnWidth}px`,
                        ...(statistic.key === 'pros' || statistic.key === 'cons'
                          ? {}
                          : { paddingTop: '0px', paddingBottom: '0px' }),
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto'
                      }}
                    >
                      <div className="flex gap-2 items-center">
                        {isEditingTickers && (
                          <button
                            onClick={isHidden ? () => onShowStatistic(statistic.key) : () => onHideStatistic(statistic.key)}
                            className={`transition-all duration-200 p-1 rounded-full active:scale-90 flex-shrink-0 ${
                              isHidden 
                                ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                            }`}
                            title={isHidden ? "Show statistic" : "Hide statistic"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {isHidden ? (
                                // Unchecked rounded square (hidden)
                                <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                              ) : (
                                // Checked rounded square (visible)
                                <>
                                  <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 12l2 2 4-4" stroke="black" className="dark:stroke-white" />
                                </>
                              )}
                            </svg>
                          </button>
                        )}
                        <div className={`${getStatisticLabelSizeClass(fontSize)} ${isHidden ? 'text-gray-600 dark:text-text-secondary opacity-50' : 'text-gray-900 dark:text-text-primary'}`}>
                          {(() => {
                            const lines = statistic.label.split('\n');
                            const lastLine = lines[lines.length - 1];
                            const otherLines = lines.slice(0, -1);
                            const lastLineWords = lastLine.split(' ');
                            const lastWord = lastLineWords[lastLineWords.length - 1];
                            const otherWords = lastLineWords.slice(0, -1);
                            
                            return (
                              <>
                                {otherLines.map((line, index) => (
                                  <div key={index}>{line}</div>
                                ))}
                                {otherLines.length > 0 && <div>
                                  {otherWords.join(' ')}
                                  {otherWords.length > 0 && ' '}
                                  <span className="whitespace-nowrap">
                                    {lastWord}
                                    {proOnlyStatistics.includes(statistic.key) && (
                                      <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                        PRO
                                      </span>
                                    )}
                                    {onIndicatorInfoClick && (!indicatorKeysWithInfo || indicatorKeysWithInfo.includes(statistic.key)) && (
                                      <IndicatorInfoButton
                                        indicatorKey={statistic.key}
                                        indicatorLabel={statistic.label}
                                        onIndicatorInfoClick={onIndicatorInfoClick}
                                        size="small"
                                      />
                                    )}
                                  </span>
                                </div>}
                                {otherLines.length === 0 && (
                                  <>
                                    {otherWords.join(' ')}
                                    {otherWords.length > 0 && ' '}
                                    <span className="whitespace-nowrap">
                                      {lastWord}
                                      {proOnlyStatistics.includes(statistic.key) && (
                                        <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                          PRO
                                        </span>
                                      )}
                                      {onIndicatorInfoClick && (!indicatorKeysWithInfo || indicatorKeysWithInfo.includes(statistic.key)) && (
                                        <button
                                          onClick={() => onIndicatorInfoClick(statistic.key)}
                                          className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full text-gray-600 dark:text-text-secondary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                          title={`Learn more about ${statistic.label}`}
                                        >
                                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                            <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                          </svg>
                                        </button>
                                      )}
                                    </span>
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </td>

                    {/* data cells for each ticker */}
                    {tickerList.map((symbol) => {
                      const ticker = getTickerData(symbol);
                      const isTickerLocked = lockedTickers.includes(symbol);
                      const colorClass = ticker ? getStatisticColor(statistic.key, ticker) : 'text-text-light-neutral dark:text-text-secondary';

                      let content: React.ReactNode = <span className="text-gray-600 dark:text-text-secondary">--</span>;
                      let formattedValue: React.ReactNode = '--';
                      if (loading) {
                        content = <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>;
                      } else if (ticker) {
                        formattedValue = formatStatisticValue(statistic.key, ticker);
                        // Only show lock if statistic is locked AND there's an actual value (not -- or N/A)
                        const isEmptyValue = formattedValue === '--' || formattedValue === 'N/A' || 
                          (typeof formattedValue === 'object' && formattedValue !== null && 
                           'props' in formattedValue && formattedValue.props?.children === '--');
                        // Lock value if ticker is locked (beyond free tier limit) or if statistic is locked
                        if (isTickerLocked && !isEmptyValue) {
                          content = <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? handleLockIconClick : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
                        } else if (lockedStatistics.includes(statistic.key) && !isEmptyValue) {
                          content = <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? handleLockIconClick : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
                        } else {
                          content = formattedValue;
                        }
                      }

                      // Allow wrapping for priceTargetRange and EPS Estimate Range (like in TickerAnalystViews)
                      // Also allow wrapping for insider transaction details (lastPurchase, lastSale, topPurchase, topSale, recentTransaction)
                      const allowWrap = statistic.key === 'priceTargetRange' || 
                                       statistic.key === 'thisQuarterEpsRange' ||
                                       statistic.key === 'nextQuarterEpsRange' ||
                                       statistic.key === 'thisYearEpsRange' ||
                                       statistic.key === 'nextYearEpsRange' ||
                                       statistic.key === 'pros' ||
                                       statistic.key === 'cons' ||
                                       statistic.key.startsWith('lastPurchase') ||
                                       statistic.key.startsWith('lastSale') ||
                                       statistic.key.startsWith('topPurchase') ||
                                       statistic.key.startsWith('topSale') ||
                                       statistic.key.startsWith('recentTransaction');
                      
                      // Check if this is a transaction detail statistic for top alignment
                      const isTransactionDetail = statistic.key.startsWith('lastPurchase') ||
                                                statistic.key.startsWith('lastSale') ||
                                                statistic.key.startsWith('topPurchase') ||
                                                statistic.key.startsWith('topSale') ||
                                                statistic.key.startsWith('recentTransaction');
                      const isProsConsDetail = statistic.key === 'pros' || statistic.key === 'cons';
                      
                      // Check if this is a LockIcon - treat it like "-" (no special alignment)
                      const isLockIcon = typeof formattedValue === 'object' && 
                        formattedValue !== null &&
                        'props' in formattedValue &&
                        formattedValue.props?.tooltipText !== undefined;
                      
                      // Check if transaction detail has real content (not "-", not LockIcon)
                      // Check formattedValue to see if it's a real transaction detail (not "-", "--", or LockIcon)
                      // Real transaction details are React elements (objects) with transaction data, not strings or LockIcon
                      const hasRealTransactionDetail = isTransactionDetail && 
                        ticker && 
                        formattedValue !== '-' && 
                        formattedValue !== '--' &&
                        !isLockIcon &&
                        typeof formattedValue === 'object' && 
                        formattedValue !== null &&
                        !('props' in formattedValue && formattedValue.props?.children === '--');
                      const hasRealProsConsDetail = isProsConsDetail &&
                        ticker &&
                        formattedValue !== '-' &&
                        formattedValue !== '--' &&
                        !isLockIcon &&
                        formattedValue !== null;
                      
                      // Determine opacity: priority order: removing > dragging > hidden > normal
                      const isRemoving = removingTicker === symbol;
                      const isDragging = draggedTicker === symbol;
                      let opacityClass = '';
                      if (isRemoving) {
                        opacityClass = 'opacity-0';
                      } else if (isDragging) {
                        opacityClass = 'opacity-60';
                      } else if (isHidden) {
                        opacityClass = 'opacity-50';
                      }
                      
                      // Determine alignment and padding for transaction details
                      // For LockIcon, treat it like "-" - use default alignment (no special class)
                      // Only apply align-top for real transaction details, align-middle for transaction detail keys with "-", default for everything else
                      // Pros/Cons: vertical padding matches label cell (including when the cell shows a lock)
                      const shouldAlignTop = isProsConsDetail || hasRealTransactionDetail || hasRealProsConsDetail;
                      const cellAlignment = shouldAlignTop ? 'align-top' : (isTransactionDetail && !isLockIcon ? 'align-middle' : '');
                      const cellPadding =
                        (isTransactionDetail && !isLockIcon)
                          ? 'py-1 sm:py-2'
                          : isProsConsDetail
                            ? prosConsCellPadding
                            : '';
                      const cellVerticalAlign = shouldAlignTop ? 'top' : (isTransactionDetail && !isLockIcon ? 'middle' : undefined);
                      
                      return (
                        <td
                          key={symbol + statistic.key}
                          className={`px-0.5 sm:px-1 md:px-2 lg:px-4 ${cellPadding} ${allowWrap ? '' : 'whitespace-nowrap'} ${cellAlignment} font-semibold ${getTextSizeClass(fontSize)} h-12 sm:h-16 transition-all duration-300 ${
                            statistic.key === 'last_closing_price' && isLastClosingPriceStuck ? 'bg-blue-50 dark:bg-gray-800' : statistic.key === 'last_closing_price' ? 'bg-surface-light dark:bg-background-card' : ''
                          } ${
                            isHidden 
                              ? 'text-gray-600 dark:text-text-secondary' 
                              : colorClass
                          } ${opacityClass} ${
                            dragOverTicker === symbol ? 'bg-highlight-gold/20 border-l-4 border-highlight-gold' : ''
                          } ${
                            isDragging ? 'scale-95 bg-highlight-gold/5' : ''
                          } ${
                            droppedTicker === symbol ? 'drop-in-animation bg-highlight-gold/10' : ''
                          }`}
                          style={cellVerticalAlign ? { verticalAlign: cellVerticalAlign } : {}}
                        >
                          {content}
                        </td>
                      );
                    })}
                    {/* Empty cell for full screen button column */}
                    <td 
                      className={`px-2 sm:px-4 h-12 sm:h-16 ${statistic.key === 'last_closing_price' && isLastClosingPriceStuck ? 'bg-blue-50 dark:bg-gray-800' : statistic.key === 'last_closing_price' ? 'bg-surface-light dark:bg-background-card' : ''}`}
                      style={statistic.key === 'last_closing_price' ? { position: 'sticky', right: 0, zIndex: 40 } : undefined}
                    ></td>
                    </tr>
                  </React.Fragment>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      {/* Status Bar */}
      <div className={`px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
        <div className="flex items-center gap-4">
          {loading && (
            <div className="flex items-center gap-2">
              <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
              <span>Loading...</span>
            </div>
          )}
          {lastUpdated && !loading && (
            <span>Last updated: {showLastUpdatedDate ? `${lastUpdated.toLocaleDateString()} ${lastUpdated.toLocaleTimeString()}` : lastUpdated.toLocaleTimeString()}</span>
          )}
          {showDelayedPricesText && <span className={`${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary`}>• 15-Minute Delayed Prices</span>}
        </div>

        {showRefreshButton && (
          <div className="flex items-center gap-2">
            <button onClick={onRefetch} className="text-highlight-gold hover:text-yellow-400 transition-colors" disabled={loading}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        )}
      </div>
    
    {/* Full Screen Overlay */}
    {isFullScreen && (
      <div
        className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
        role="dialog"
        aria-modal="true"
      >
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain">
            <table 
              className="min-w-full font-mono" 
              style={{ 
                tableLayout: 'fixed',
                borderCollapse: 'collapse'
              }}
            >
              <colgroup>
                <col style={{ 
                  width: `${fullScreenIndicatorsColumnWidth}px`, 
                  minWidth: `${fullScreenIndicatorsColumnWidth}px` 
                }} />
                {tickerList.map((_, index) => {
                  // Reduce ticker column width on small screens in fullscreen mode too
                  const effectiveTickerWidth = isSmallScreen 
                    ? `${Math.max(100, parseInt(tickerColumnWidth.replace('px', '')) * 0.75)}px` // 75% of original width, minimum 100px
                    : tickerColumnWidth;
                  return (
                    <col key={index} style={{ width: effectiveTickerWidth, minWidth: effectiveTickerWidth }} />
                  );
                })}
              </colgroup>
              
              {/* Header with sticky positioning */}
              <thead className="border-b border-gray-200 dark:border-border-color" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                <tr className="border-b border-gray-200 dark:border-border-color h-12 sm:h-16">
                  {/* Statistics header - sticky left */}
                  <th 
                    className={`px-2 sm:px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider bg-gray-50 dark:bg-background-card h-12 sm:h-16`}
                    style={{ position: 'sticky', left: 0, zIndex: 40 }}
                  >
                    <div className="flex items-center h-full">
                      Indicators
                    </div>
                  </th>
                  
                  {/* Ticker headers */}
                  {tickerList.map((symbol) => {
                    const ticker = getTickerData(symbol);
                    return (
                      <th
                        key={symbol}
                        className={`px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider bg-gray-50 dark:bg-background-card h-12 sm:h-16`}
                      >
                        <div className="relative group/button flex items-center h-full">
                          <span className="font-semibold text-gray-900 dark:text-text-primary">
                            {symbol}
                          </span>
                          <div className="absolute hidden group-hover/button:block left-0 top-full mt-1 w-max bg-white dark:bg-background-card border border-gray-200 dark:border-border-color rounded-md shadow-lg p-2 text-xs text-gray-600 dark:text-text-secondary z-[60] font-sans">
                            {ticker?.ticker_name || ticker?.name || symbol}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                  
                  {/* Close button - sticky right */}
                  <th 
                    className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                    style={{ position: 'sticky', right: 0, zIndex: 40 }}
                  >
                    <button
                      onClick={() => setIsFullScreen(false)}
                      className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                      title="Close full screen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </th>
                </tr>
              </thead>
              
              {/* Table body with sticky first column */}
              <tbody className="divide-y divide-gray-200 dark:divide-border-color">
                {(() => {
                  const filteredStats = statistics.filter(statistic => isEditingTickers || !hiddenStatistics.includes(statistic.key));
                  
                  // Guard against undefined categoryHeaders
                  const baseHeaders = categoryHeaders || [];
                  
                  // Pre-calculate category headers like TickerFundamentals and TickerAnalystViews do
                  // This approach explicitly handles empty categories (startIndex: -1) and filters them out
                  // IMPORTANT: This calculation runs on EVERY render, so it will update when hiddenStatistics changes
                  const calculatedHeaders = baseHeaders.map(header => {
                    if (isEditingTickers) {
                      // In edit mode, use original startIndex since we're showing all statistics
                      // Find the position in filteredStats (which includes all stats in edit mode)
                      const statAtOriginalIndex = statistics[header.startIndex];
                      if (!statAtOriginalIndex) {
                        return { ...header, startIndex: -1 };
                      }
                      const newStartIndex = filteredStats.findIndex(stat => stat.key === statAtOriginalIndex.key);
                      return {
                        ...header,
                        startIndex: newStartIndex >= 0 ? newStartIndex : -1
                      };
                    } else {
                      // In normal mode, find the first visible statistic in this category
                      // Use the EXACT SAME filter logic as filteredStats to ensure consistency
                      const nextCategoryStartIndex = baseHeaders.find(h => h.startIndex > header.startIndex)?.startIndex || statistics.length;
                      const categoryStatistics = statistics.slice(header.startIndex, nextCategoryStartIndex);
                      
                      // IMPORTANT: Use the EXACT SAME visibility check as filteredStats
                      // A stat is visible if: isEditingTickers OR not in hiddenStatistics
                      // Since we're in normal mode (not editing), isEditingTickers is false, so we check !hiddenStatistics.includes(stat.key)
                      const visibleStatsInCategory = categoryStatistics.filter(stat => !hiddenStatistics.includes(stat.key));
                      
                      if (visibleStatsInCategory.length === 0) {
                        // No visible statistics in this category - mark for exclusion
                        return { 
                          ...header, 
                          startIndex: -1 
                        };
                      }
                      
                      // Get the first visible statistic
                      const firstVisibleStatistic = visibleStatsInCategory[0];
                      
                      // Find the position of this statistic in the filtered array
                      // This should always succeed since we use the same filter logic
                      const newStartIndex = filteredStats.findIndex(stat => stat.key === firstVisibleStatistic.key);
                      
                      if (newStartIndex < 0) {
                        // This shouldn't happen - if stat is visible, it should be in filteredStats
                        console.error(`⚠️ [StockTable] Category "${header.label}": First visible stat ${firstVisibleStatistic.key} not found in filteredStats! This is a bug.`);
                        return { 
                          ...header, 
                          startIndex: -1 
                        };
                      }
                      
                      return {
                        ...header,
                        startIndex: newStartIndex
                      };
                    }
                  }).filter(header => {
                    // In edit mode, show all categories. In normal mode, only show categories with visible statistics
                    const shouldShow = isEditingTickers || header.startIndex !== -1;
                    return shouldShow;
                  });
                  
                  // Track which section headers have already been shown to avoid duplicates
                  const shownHeaders = new Set<string>();
                  
                  // Now render the statistics with pre-calculated headers
                  return filteredStats.map((statistic, index) => {
                      // Check if we need to render a category header before this statistic
                      // Find the header that should appear at this index
                      const categoryHeader = calculatedHeaders.find(header => {
                        // Skip if this header has already been shown
                        if (shownHeaders.has(header.label)) {
                          return false;
                        }
                        
                        // Check if this statistic's index matches the header's startIndex
                        if (index !== header.startIndex) {
                          return false;
                        }
                        
                        // Mark the header as shown
                        shownHeaders.add(header.label);
                        return true;
                      });
                      
                      // Category header is already marked as shown in the find() above if it matches
                      const shouldShowCategoryHeader = !!categoryHeader;
                      
                      return (
                        <React.Fragment key={statistic.key}>
                             {/* Category header row */}
                             {shouldShowCategoryHeader && (
                               <tr className="bg-gray-100 dark:bg-border-color">
                                 <td
                                   className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                   style={{ 
                                     position: 'sticky', 
                                     left: 0, 
                                     zIndex: 30,
                                     width: `${statisticsColumnWidth}px`,
                                     maxWidth: `${statisticsColumnWidth}px`
                                   }}
                                 >
                                   <div 
                                     className="whitespace-nowrap"
                                     style={{ overflow: 'visible', position: 'relative' }}
                                   >
                                     <span className="inline">{categoryHeader.label}</span>
                                     {onCategoryInfoClick && (
                                       <>
                                         <span className="inline ml-1"></span>
                                         <button
                                           onClick={() => onCategoryInfoClick(categoryHeader.label)}
                                           className="inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                           title={`Learn more about ${categoryHeader.label}`}
                                         >
                                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                             <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                             <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                             <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                           </svg>
                                         </button>
                                       </>
                                     )}
                                   </div>
                                 </td>
                                 {tickerList.map((_, index) => (
                                   <td
                                     key={index}
                                     className={`px-1 sm:px-2 md:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                                   >
                                     {/* Empty cells for alignment */}
                                   </td>
                                 ))}
                                 {/* Empty cell for close button column */}
                                 <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}></td>
                               </tr>
                             )}
                          
                          {/* Statistic row */}
                          <tr 
                            ref={statistic.key === 'last_closing_price' ? lastClosingPriceRowFullScreenRef : null}
                            className={`hover:bg-gray-50 dark:hover:bg-background-card ${statistic.key === 'last_closing_price' ? `sticky top-[47px] sm:top-[63px] z-40 sticky-last-closing-price ${isLastClosingPriceStuckFullScreen ? 'is-stuck' : ''}` : ''}`}
                          >
                            {/* Statistic label - sticky left */}
                            <td
                              className={`${statistic.key === 'pros' || statistic.key === 'cons' ? prosConsCellPadding : 'py-1 sm:py-2'} px-2 sm:px-4 ${getStatisticLabelSizeClass(fontSize)} font-medium text-gray-900 dark:text-text-primary ${statistic.key === 'last_closing_price' && isLastClosingPriceStuckFullScreen ? 'bg-blue-50 dark:bg-gray-800' : 'bg-white dark:bg-background'} ${statistic.key === 'last_closing_price' ? 'sticky top-[47px] sm:top-[63px]' : ''} ${statistic.key === 'pros' || statistic.key === 'cons' ? 'align-top' : ''}`}
                              style={{ 
                                position: 'sticky', 
                                left: 0, 
                                zIndex: statistic.key === 'last_closing_price' ? 50 : 30
                              }}
                            >
                              <div className={`flex gap-2 flex-wrap ${statistic.key === 'pros' || statistic.key === 'cons' ? 'items-start' : 'items-center'}`}>
                                {isEditingTickers && (
                                  <button
                                    onClick={hiddenStatistics.includes(statistic.key) 
                                      ? () => onShowStatistic(statistic.key)
                                      : () => onHideStatistic(statistic.key)
                                    }
                                    className="p-1 text-gray-500 hover:text-highlight-gold transition-colors duration-200 flex-shrink-0"
                                    title={hiddenStatistics.includes(statistic.key) ? "Show statistic" : "Hide statistic"}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      {hiddenStatistics.includes(statistic.key) ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                                      )}
                                    </svg>
                                  </button>
                                )}
                                <span className={`${getTextSizeClass(fontSize)} break-words`}>
                                  {(() => {
                                    const words = statistic.label.split(' ');
                                    const lastWord = words[words.length - 1];
                                    const otherWords = words.slice(0, -1);
                                    const shouldShowInfoButton = onIndicatorInfoClick && (!indicatorKeysWithInfo || indicatorKeysWithInfo.includes(statistic.key));
                                    
                                    return (
                                      <>
                                        {otherWords.join(' ')}
                                        {otherWords.length > 0 && ' '}
                                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                                          {lastWord}
                                          {proOnlyStatistics.includes(statistic.key) && (
                                            <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded whitespace-nowrap">
                                              PRO
                                            </span>
                                          )}
                                          {shouldShowInfoButton && (
                                            <IndicatorInfoButton
                                              indicatorKey={statistic.key}
                                              indicatorLabel={statistic.label}
                                              onIndicatorInfoClick={onIndicatorInfoClick}
                                              size="small"
                                            />
                                          )}
                                        </span>
                                      </>
                                    );
                                  })()}
                                </span>
                              </div>
                            </td>
                            
                               {/* Ticker data cells */}
                               {tickerList.map((symbol) => {
                                 const ticker = getTickerData(symbol);
                                 const isTickerLocked = lockedTickers.includes(symbol);
                                 let value: React.ReactNode = '-';
                                 let formattedValue: React.ReactNode = '-';
                                 if (ticker) {
                                   formattedValue = formatStatisticValue(statistic.key, ticker);
                                   // Only show lock if statistic is locked AND there's an actual value (not -- or N/A)
                                   const isEmptyValue = formattedValue === '--' || formattedValue === 'N/A' || formattedValue === '-' ||
                                     (typeof formattedValue === 'object' && formattedValue !== null && 
                                      'props' in formattedValue && formattedValue.props?.children === '--');
                                   // Lock value if ticker is locked (beyond free tier limit) or if statistic is locked
                                   if (isTickerLocked && !isEmptyValue) {
                                     value = <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? handleLockIconClick : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
                                   } else if (lockedStatistics.includes(statistic.key) && !isEmptyValue) {
                                     value = <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? handleLockIconClick : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
                                   } else {
                                     value = formattedValue;
                                   }
                                 }
                                 const color = ticker ? getStatisticColor(statistic.key, ticker) : 'text-gray-500 dark:text-text-secondary';
                                 
                                 // Allow wrapping for priceTargetRange and EPS Estimate Range (like in TickerAnalystViews)
                                 // Also allow wrapping for insider transaction details (lastPurchase, lastSale, topPurchase, topSale, recentTransaction)
                                 const allowWrap = statistic.key === 'priceTargetRange' || 
                                                  statistic.key === 'thisQuarterEpsRange' ||
                                                  statistic.key === 'nextQuarterEpsRange' ||
                                                  statistic.key === 'thisYearEpsRange' ||
                                                  statistic.key === 'nextYearEpsRange' ||
                                                  statistic.key === 'pros' ||
                                                  statistic.key === 'cons' ||
                                                  statistic.key.startsWith('lastPurchase') ||
                                                  statistic.key.startsWith('lastSale') ||
                                                  statistic.key.startsWith('topPurchase') ||
                                                  statistic.key.startsWith('topSale') ||
                                                  statistic.key.startsWith('recentTransaction');
                                 
                                 // Check if this is a transaction detail statistic for top alignment
                                 const isTransactionDetail = statistic.key.startsWith('lastPurchase') ||
                                                           statistic.key.startsWith('lastSale') ||
                                                           statistic.key.startsWith('topPurchase') ||
                                                           statistic.key.startsWith('topSale') ||
                                                           statistic.key.startsWith('recentTransaction');
                                 const isProsConsDetail = statistic.key === 'pros' || statistic.key === 'cons';
                                 
                                 // Check if transaction detail has real content (not "-" or LockIcon)
                                 // Check formattedValue to see if it's a real transaction detail (not "-", "--", or LockIcon)
                                 // Real transaction details are React elements (objects) with transaction data, not strings or LockIcon
                                 // LockIcon has tooltipText prop, so we can detect it that way
                                 const isLockIcon = typeof formattedValue === 'object' && 
                                   formattedValue !== null &&
                                   'props' in formattedValue &&
                                   formattedValue.props?.tooltipText !== undefined;
                                 
                                 const hasRealTransactionDetail = isTransactionDetail && 
                                   ticker && 
                                   formattedValue !== '-' && 
                                   formattedValue !== '--' &&
                                   !isLockIcon &&
                                   typeof formattedValue === 'object' && 
                                   formattedValue !== null &&
                                   !('props' in formattedValue && formattedValue.props?.children === '--');
                                 const hasRealProsConsDetail = isProsConsDetail &&
                                   ticker &&
                                   formattedValue !== '-' &&
                                   formattedValue !== '--' &&
                                   !isLockIcon &&
                                   formattedValue !== null;
                                 
                                 // Determine alignment for transaction details
                                 // For LockIcon, treat it like "-" - use default alignment (no special class)
                                 // Only apply align-top for real transaction details, align-middle for transaction detail keys with "-", default for everything else
                                 const shouldAlignTop = isProsConsDetail || hasRealTransactionDetail || hasRealProsConsDetail;
                                 const cellAlignment = shouldAlignTop ? 'align-top' : (isTransactionDetail && !isLockIcon ? 'align-middle' : '');
                                 const cellVerticalAlign = shouldAlignTop ? 'top' : (isTransactionDetail && !isLockIcon ? 'middle' : undefined);
                                 
                                 return (
                                   <td
                                     key={symbol}
                                     className={`${isProsConsDetail ? prosConsCellPadding : 'py-1 sm:py-2'} px-1 sm:px-2 md:px-4 lg:px-6 ${allowWrap ? '' : 'whitespace-nowrap'} ${cellAlignment} ${getTextSizeClass(fontSize)} font-medium ${color} ${statistic.key === 'last_closing_price' && isLastClosingPriceStuckFullScreen ? 'bg-blue-50 dark:bg-gray-800' : 'bg-white dark:bg-background'} ${statistic.key === 'last_closing_price' ? 'sticky top-[47px] sm:top-[63px] z-40' : ''}`}
                                     style={statistic.key === 'last_closing_price' ? { position: 'sticky', zIndex: 40 } : (cellVerticalAlign ? { verticalAlign: cellVerticalAlign } : undefined)}
                                   >
                                     {value}
                                   </td>
                                 );
                               })}
                               
                               {/* Empty cell for close button column */}
                               <td 
                                 className={`py-2 px-4 ${statistic.key === 'last_closing_price' && isLastClosingPriceStuckFullScreen ? 'bg-blue-50 dark:bg-gray-800' : 'bg-white dark:bg-background'} ${statistic.key === 'last_closing_price' ? 'sticky top-[47px] sm:top-[63px] z-40' : ''}`}
                                 style={statistic.key === 'last_closing_price' ? { position: 'sticky', zIndex: 40 } : undefined}
                               ></td>
                          </tr>
                        </React.Fragment>
                      );
                    });
                })()}
              </tbody>
            </table>
        </div>
      </div>
    )}
  </>
  );
};

export default StockTable;
